import React, { useEffect, useRef } from 'react';
import gsap from 'gsap';

// ── Magnet ───────────────────────────────────────────────────────────────────
// Wraps a button/CTA so it drifts toward the cursor within `padding` px, then
// eases back on release — adapted from React Bits' Magnet component
// (https://reactbits.dev/animations/magnet), swapped from React state + CSS
// transitions to gsap.quickTo so the drag reads as buttery rather than
// spring-laggy. No-ops on touch devices and when the user prefers less motion.
export default function Magnet({
  children,
  padding = 90,
  strength = 6,
  disabled = false,
  className = '',
  style,
  ...props
}) {
  const wrapRef = useRef(null);
  const innerRef = useRef(null);
  const quickRef = useRef(null);

  useEffect(() => {
    const isTouch = window.matchMedia('(pointer: coarse)').matches;
    const reduced = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    if (disabled || isTouch || reduced || !innerRef.current) return;

    const x = gsap.quickTo(innerRef.current, 'x', { duration: 0.5, ease: 'power3.out' });
    const y = gsap.quickTo(innerRef.current, 'y', { duration: 0.5, ease: 'power3.out' });
    quickRef.current = { x, y };

    // getBoundingClientRect() is a synchronous layout read — calling it on
    // every mousemove (as this did before) means every pointer move
    // anywhere on the page pays a layout cost for as long as any Magnet is
    // mounted (the landing page has two: Hero CTA + Final CTA), even while
    // the cursor is nowhere near either button. Cached instead, and only
    // refreshed on scroll/resize — the button's position doesn't change
    // between those.
    let rect = wrapRef.current.getBoundingClientRect();
    const refreshRect = () => { if (wrapRef.current) rect = wrapRef.current.getBoundingClientRect(); };
    window.addEventListener('scroll', refreshRect, { passive: true });
    window.addEventListener('resize', refreshRect);

    const onMove = (e) => {
      const cx = rect.left + rect.width / 2;
      const cy = rect.top + rect.height / 2;
      const distX = e.clientX - cx;
      const distY = e.clientY - cy;
      if (Math.abs(distX) < rect.width / 2 + padding && Math.abs(distY) < rect.height / 2 + padding) {
        x(distX / strength);
        y(distY / strength);
      } else {
        x(0);
        y(0);
      }
    };
    window.addEventListener('mousemove', onMove, { passive: true });
    return () => {
      window.removeEventListener('mousemove', onMove);
      window.removeEventListener('scroll', refreshRect);
      window.removeEventListener('resize', refreshRect);
      gsap.killTweensOf(innerRef.current);
    };
  }, [disabled, padding, strength]);

  return (
    <span ref={wrapRef} className={className} style={{ display: 'inline-block', ...style }}>
      <span ref={innerRef} style={{ display: 'inline-block', willChange: 'transform' }} {...props}>
        {children}
      </span>
    </span>
  );
}
